import { ApolloError } from "@apollo/client";

export function isValidCn(value: string): boolean {
  return value.startsWith("CN");
}

/**
 * Check if an Apollo error is a duplicate CN error
 */
export function isDuplicateCnError(error: ApolloError): boolean {
  return error.graphQLErrors?.some((e) => e.extensions?.code === "DUPLICATE_CN") ?? false;
}

/**
 * Parse a search string into individual search terms
 */
export function parseSearchTerms(input: string | undefined): string[] {
  if (!input?.trim()) return [];
  return input
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

type MappingForFilter = {
  alternateIdInfo: {
    alternateIdValue: string;
    tenant?: { name?: string | null } | null;
    description?: string | null;
  };
};

/**
 * Filter mappings based on search terms (OR logic - any term can match)
 */
export function filterMappings<T extends MappingForFilter>(
  mappings: T[],
  searchTerms: string[]
): T[] {
  if (searchTerms.length === 0) return mappings;

  // Lowercase terms for case-insensitive matching
  const lowerTerms = searchTerms.map((t) => t.toLowerCase());

  return mappings.filter((m) => {
    const cn = m.alternateIdInfo.alternateIdValue.toLowerCase();
    const tenant = (m.alternateIdInfo.tenant?.name ?? "").toLowerCase();
    const description = (m.alternateIdInfo.description ?? "").toLowerCase();
    const allText = `${cn} ${tenant} ${description}`;

    // Any search term can match (OR logic)
    return lowerTerms.some((term) => allText.includes(term));
  });
}

type CredentialForFilter = {
  username: string;
  name?: string | null;
};

/**
 * Filter credentials based on filter terms (OR logic - any term can match)
 */
export function filterCredentials<T extends CredentialForFilter>(
  credentials: T[],
  filterTerms: string[]
): T[] {
  if (filterTerms.length === 0) return credentials;

  // Lowercase terms for case-insensitive matching
  const lowerTerms = filterTerms.map((t) => t.toLowerCase());

  return credentials.filter((c) => {
    const searchText = `${c.username} ${c.name ?? ""}`.toLowerCase();
    return lowerTerms.some((term) => searchText.includes(term));
  });
}

type CredentialWithId = {
  id: string;
};

type MappingWithAssociations = {
  associatedCredentialIds: string[];
};

/**
 * Filter credentials to only those that have an association with at least one
 * of the provided mappings
 */
export function filterCredentialsByAssociation<T extends CredentialWithId>(
  credentials: T[],
  mappings: MappingWithAssociations[]
): T[] {
  if (mappings.length === 0) return [];

  const allAssociatedIds = new Set<string>();
  for (const mapping of mappings) {
    for (const id of mapping.associatedCredentialIds) {
      allAssociatedIds.add(id);
    }
  }

  return credentials.filter((c) => allAssociatedIds.has(c.id));
}
